import React, { useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  addSubPropertyHistory,
  updateSubPropertyHistory,
} from 'services/subPropertyService';
// no file upload needed for jantri
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  subPropertyId: string;
  onSuccess: () => void;
  editData?: any;
}

interface FormValues {
  rentAmount: number;
  annualRentIncrementPercentage: number;
  jantri: number;
}

const validationSchema = Yup.object().shape({
  rentAmount: Yup.number()
    .integer('Rent amount must be a whole number') // ✅ disallows decimals
    .positive('Rent amount must be greater than 0') // ✅ only positive,
    .required(),
  annualRentIncrementPercentage: Yup.number()
    .integer('Annual rent increment percentage must be a whole number') // ✅ disallows decimals
    .positive('Annual rent increment percentage must be greater than 0') // ✅ only positive
    .required(),
  jantri: Yup.number()
    .integer('Jantri must be a whole number') // ✅ disallows decimals
    .positive('Jantri must be greater than 0') // ✅ only positive
    .required(),
});

const HistoryModal: React.FC<Props> = ({
  isOpen,
  toggle,
  subPropertyId,
  onSuccess,
  editData,
}) => {
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      rentAmount: 0,
      annualRentIncrementPercentage: 0,
      jantri: 0,
    },
  });

  // removed file upload logic

  const onSubmit = async (values: FormValues) => {
    dispatch(isVisible(true));
    try {
      const payload = { ...values, subPropertyId };
      const res = editData
        ? await updateSubPropertyHistory(editData.id, payload)
        : await addSubPropertyHistory(payload);
      if (res?.data) {
        toast.success(`History ${editData ? 'updated' : 'added'} successfully`);
        onSuccess();
        reset();
        // removed file upload logic
        toggle();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      dispatch(isVisible(false));
    }
  };

  // populate
  useEffect(() => {
    if (isOpen && editData) {
      reset({
        rentAmount: editData.rentAmount,
        annualRentIncrementPercentage: editData.annualRentIncrementPercentage,
        jantri: editData.jantri,
      });
    }
    if (!isOpen) {
      reset();
    }
  }, [isOpen, editData, reset]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {editData ? 'Edit' : 'Add'} Configuration
      </ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className="overflow-hidden">
          <FormGroup>
            <Label>Rent Amount</Label>
            <Controller
              name="rentAmount"
              control={control}
              render={({ field }) => <Input {...field} type="number" />}
            />
            {errors.rentAmount && (
              <span className="text-danger">{errors.rentAmount.message}</span>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Annual Rent Increment (%)</Label>
            <Controller
              name="annualRentIncrementPercentage"
              control={control}
              render={({ field }) => <Input {...field} type="number" />}
            />
            {errors.annualRentIncrementPercentage && (
              <span className="text-danger">
                {errors.annualRentIncrementPercentage.message}
              </span>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Jantri (₹)</Label>
            <Controller
              name="jantri"
              control={control}
              render={({ field }) => <Input {...field} type="number" />}
            />
            {errors.jantri && (
              <span className="text-danger">{errors.jantri.message}</span>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} type="button">
            Cancel
          </Button>
          <Button color="primary" type="submit">
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default HistoryModal;
